import React, { useMemo, useState } from "react";
import { toast } from "sonner";
import { CheckCircle2, Loader2, MessageCircle, X } from "lucide-react";
import { usersApi } from "../../lib/api";
import { getApiError } from "../../lib/utils";
import { TID } from "../../constants/testIds";

const PRESET_LABELS = {
  viewer: "Viewer",
  operator: "Operator",
  admin: "Admin",
  super: "Super (all)",
};

/**
 * Modal to grant/revoke individual bot commands for a user.
 * Split out of the old inline Users.jsx version so both the unified
 * Users page and any future surface can reuse it.
 *
 * @param {object} props
 * @param {object} props.user
 * @param {object} props.taxonomy   — /users/telegram-permissions/taxonomy
 * @param {() => void} props.onClose
 * @param {() => void} props.onSaved
 */
export default function EditTelegramPermissionsModal({ user, taxonomy, onClose, onSaved }) {
  const initial = useMemo(() => {
    if (Array.isArray(user.telegram_permissions) && user.telegram_permissions.length) {
      return new Set(user.telegram_permissions);
    }
    const roleKey =
      user.role === "SUPER_ADMIN" ? "super" : user.role === "ADMIN" ? "admin" : "viewer";
    return new Set(taxonomy?.presets?.[roleKey] || []);
  }, [user, taxonomy]);

  const [enabled, setEnabled] = useState(initial);
  const [saving, setSaving] = useState(false);

  const toggle = (key) =>
    setEnabled((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });

  const applyPreset = (name) => {
    setEnabled(new Set(taxonomy?.presets?.[name] || []));
    toast.success(`Applied ${PRESET_LABELS[name] || name} preset`);
  };

  const clearAll = () => setEnabled(new Set());

  const save = async () => {
    setSaving(true);
    try {
      await usersApi.update(user.user_id, { telegram_permissions: Array.from(enabled) });
      toast.success(`Updated ${user.display_name}'s Telegram permissions`);
      onSaved();
    } catch (e) {
      toast.error(getApiError(e, "Update failed"));
    } finally {
      setSaving(false);
    }
  };

  const grouped = useMemo(() => {
    const g = {};
    (taxonomy?.permissions || []).forEach((p) => { (g[p.feature] ||= []).push(p); });
    return g;
  }, [taxonomy]);

  const total = (taxonomy?.permissions || []).length;

  return (
    <div
      data-testid={TID.tgPermsModal}
      className="fixed inset-0 z-[60] flex items-center justify-center bg-slate-900/50 backdrop-blur-sm animate-fade-in p-4"
      onClick={onClose}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="w-full max-w-3xl max-h-[90vh] overflow-hidden rounded-md border border-slate-200 bg-white shadow-2xl flex flex-col"
      >
        <div className="flex items-start justify-between border-b border-slate-200 px-6 py-4">
          <div>
            <div className="flex items-center gap-2">
              <MessageCircle size={16} className="text-nmi-700" strokeWidth={2.5} />
              <h3 className="font-heading text-lg font-bold text-slate-900">Telegram permissions</h3>
            </div>
            <p className="mt-0.5 text-xs text-slate-600">
              Editing <b>{user.display_name}</b>
              {user.telegram_username && <span className="text-slate-400"> · @{user.telegram_username}</span>}
              {user.telegram_id && <span className="text-slate-400 ml-1 font-mono">({user.telegram_id})</span>}
            </p>
            <p className="mt-1 text-[10px] text-slate-500">
              Governs what this user can do <b>inside the Telegram bot</b>.
            </p>
          </div>
          <button onClick={onClose} className="rounded-md p-1.5 text-slate-500 hover:bg-slate-100">
            <X size={18} strokeWidth={2.5} />
          </button>
        </div>

        <div className="border-b border-slate-200 bg-slate-50 px-6 py-3">
          <div className="flex flex-wrap items-center gap-2">
            <span className="text-[10px] font-bold uppercase tracking-[0.2em] text-slate-500 mr-1">Presets</span>
            {Object.keys(PRESET_LABELS).map((p) => (
              <button
                key={p}
                data-testid={TID.tgPermsPreset(p)}
                onClick={() => applyPreset(p)}
                className="rounded-md border border-slate-200 bg-white px-2.5 py-1 text-[11px] font-semibold text-slate-700 hover:border-nmi-600 hover:text-nmi-700"
              >
                {PRESET_LABELS[p]}
              </button>
            ))}
            <button
              onClick={clearAll}
              className="ml-auto rounded-md border border-slate-200 bg-white px-2.5 py-1 text-[11px] font-semibold text-slate-500 hover:border-red-300 hover:text-red-700"
            >
              Clear all
            </button>
          </div>
        </div>

        <div className="flex-1 overflow-y-auto px-6 py-5 space-y-5">
          {Object.entries(grouped).map(([feature, perms]) => (
            <div key={feature}>
              <div className="mb-2 text-[10px] font-bold uppercase tracking-[0.2em] text-slate-500">{feature}</div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                {perms.map((p) => {
                  const on = enabled.has(p.key);
                  return (
                    <label
                      key={p.key}
                      className={`flex cursor-pointer items-start gap-2.5 rounded-md border px-3 py-2.5 transition-colors ${
                        on ? "border-nmi-600 bg-nmi-50" : "border-slate-200 bg-white hover:border-slate-300"
                      }`}
                    >
                      <input
                        type="checkbox"
                        data-testid={TID.tgPermsCheckbox(p.key)}
                        checked={on}
                        onChange={() => toggle(p.key)}
                        className="mt-0.5 h-4 w-4 rounded border-slate-300 text-nmi-700 focus:ring-nmi-600"
                      />
                      <div className="flex-1 min-w-0">
                        <div className="text-sm font-semibold text-slate-900">{p.label}</div>
                        <div className="text-[11px] text-slate-600">{p.description}</div>
                        <code className="mt-0.5 block truncate text-[10px] font-mono text-slate-400">{p.key}</code>
                      </div>
                    </label>
                  );
                })}
              </div>
            </div>
          ))}
        </div>

        <div className="flex items-center justify-between border-t border-slate-200 bg-slate-50 px-6 py-3">
          <div data-testid={TID.tgPermsCount} className="text-xs text-slate-600">
            <b>{enabled.size}</b> / {total} permissions enabled
          </div>
          <div className="flex gap-2">
            <button
              data-testid={TID.tgPermsCancel}
              onClick={onClose}
              disabled={saving}
              className="rounded-md border border-slate-200 bg-white px-3 py-1.5 text-xs font-semibold text-slate-700 hover:bg-slate-50 disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              data-testid={TID.tgPermsSave}
              onClick={save}
              disabled={saving}
              className="inline-flex items-center gap-1.5 rounded-md bg-nmi-700 px-3 py-1.5 text-xs font-semibold text-white hover:bg-nmi-800 disabled:opacity-60"
            >
              {saving ? <Loader2 size={12} className="animate-spin" /> : <CheckCircle2 size={12} />}
              Save permissions
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
